import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { formatCurrencySpaced } from '@/lib/market';
import {
  Home, Coins, Wallet,
  MapPin, ArrowUpRight, ArrowDownRight, Palette, RefreshCw, Brain, Plus, ChevronRight, BarChart3
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';

const CHART_PALETTE = ['#ffd879','#d9e2c3','#a8c5da','#c9a86b','#9b8dc4','#8dc4a8','#c4a88d','#8da8c4','#b8c4a8','#c4b88d','#c4c48d','#a8a8c4'];

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (v, market) => formatCurrencySpaced(v, market);

const GainBadge = ({ pct }) => (
  <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${pct >= 0 ? 'text-[#ffd879]' : 'text-[#c97a3a]'}`}>
    {pct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
    {Math.abs(pct).toFixed(1)}%
  </span>
);

const StatCard = ({ label, value, sub }) => (
  <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm h-full">
    <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">{label}</p>
    <p className="text-lg font-bold text-[#1a1a1a]">{value}</p>
    {sub && <p className="text-xs text-[#4a4a4a] mt-0.5">{sub}</p>}
  </div>
);

// ── Tab components ────────────────────────────────────────────────────────────

function RealEstateTab({ data, market }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <Home className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
        <p className="text-[#4a4a4a] text-sm">No real estate properties found</p>
      </div>
    );
  }

  const totalValue    = data.reduce((s, p) => s + (p.current_value || 0), 0);
  const totalInvested = data.reduce((s, p) => s + (p.purchase_price || 0), 0);
  const totalRent     = data.reduce((s, p) => s + ((p.monthly_rent || 0) * 12), 0);
  const avgYield      = totalValue > 0 ? (totalRent / totalValue * 100).toFixed(1) : '0.0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Value"     value={fmt(totalValue, market)}    sub={`${data.length} properties`} />
        <StatCard label="Total Invested"  value={fmt(totalInvested, market)} sub="cost basis" />
        <StatCard label="Annual Rent"     value={fmt(totalRent, market)}     sub="across rented properties" />
        <StatCard label="Portfolio Yield" value={`${avgYield}%`}             sub="gross rental yield" />
      </div>

      <div className="space-y-3">
        {data.map((p, i) => {
          const gainPct  = p.gain_percent ?? 0;
          const yieldPct = p.rental_yield ?? 0;
          const propType = p.property_type
            ? p.property_type.charAt(0).toUpperCase() + p.property_type.slice(1)
            : '—';
          const statusLabel = p.is_rented ? 'Rented' : 'Vacant';

          return (
            <Card key={i} className="bg-white border-[#e5e5e5]">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#ffd879]/15 flex items-center justify-center shrink-0">
                      <Home className="w-5 h-5 text-[#ffd879]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1a1a1a] text-sm">{p.name}</p>
                      <p className="text-xs text-[#4a4a4a] flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" />{p.city} · {propType}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-right shrink-0">
                    <div>
                      <p className="text-sm font-bold text-[#1a1a1a]">{fmt(p.current_value, market)}</p>
                      <GainBadge pct={gainPct} />
                    </div>
                    <div>
                      <p className="text-xs text-[#4a4a4a]">Yield</p>
                      <p className="text-sm font-medium text-[#2a2a2a]">{yieldPct > 0 ? `${yieldPct.toFixed(1)}%` : '—'}</p>
                    </div>
                    <Badge className={`text-xs border-0 ${
                      p.is_rented ? 'bg-emerald-100 text-emerald-700' : 'bg-[#f9f9f9] text-[#4a4a4a]'
                    }`}>{statusLabel}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function GoldTab({ data, market }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <Coins className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
        <p className="text-[#4a4a4a] text-sm">No gold & commodities holdings found</p>
      </div>
    );
  }

  const totalValue    = data.reduce((s, g) => s + (g.units * g.current_price), 0);
  const totalInvested = data.reduce((s, g) => s + (g.units * g.unit_price), 0);
  const avgGain       = totalInvested > 0 ? ((totalValue - totalInvested) / totalInvested * 100).toFixed(1) : '0.0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Value"    value={fmt(totalValue, market)}    sub={`${data.length} holdings`} />
        <StatCard label="Total Invested" value={fmt(totalInvested, market)} sub="cost basis" />
        <StatCard label="Overall Gain"   value={<GainBadge pct={parseFloat(avgGain)} />} sub="since purchase" />
      </div>

      <div className="border border-[#e5e5e5] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#dae4c4]">
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Instrument</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Type</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Units</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Avg Cost</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Current Price</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Value</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Gain</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Maturity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e5e5e5] bg-white">
            {data.map((g, i) => (
              <tr key={i} className="hover:bg-[#f9f9f9] transition-colors">
                <td className="px-4 py-3 font-medium text-[#1a1a1a]">
                  <div className="flex items-center gap-2">
                    <Coins className="w-3.5 h-3.5 text-[#ffd879] shrink-0" />{g.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <Badge className="bg-[#ffd879]/20 text-[#1a1a1a] border-0 text-xs">{g.type}</Badge>
                </td>
                <td className="px-4 py-3 text-right text-[#4a4a4a]">{g.units}</td>
                <td className="px-4 py-3 text-right text-[#4a4a4a]">{fmt(g.unit_price, market)}</td>
                <td className="px-4 py-3 text-right text-[#2a2a2a]">{fmt(g.current_price, market)}</td>
                <td className="px-4 py-3 text-right font-semibold text-[#1a1a1a]">{fmt(g.units * g.current_price, market)}</td>
                <td className="px-4 py-3 text-right"><GainBadge pct={g.gain_pct} /></td>
                <td className="px-4 py-3 text-right text-xs text-[#4a4a4a]">{g.maturity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ArtTab({ data, market }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <Palette className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
        <p className="text-[#4a4a4a] text-sm">No art & collectibles found</p>
      </div>
    );
  }

  const totalValue    = data.reduce((s, a) => s + (a.current_value || 0), 0);
  const totalInvested = data.reduce((s, a) => s + (a.purchase_price || 0), 0);
  const avgGain       = totalInvested > 0 ? ((totalValue - totalInvested) / totalInvested * 100).toFixed(1) : '0.0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Collection Value" value={fmt(totalValue, market)}                    sub={`${data.length} pieces`} />
        <StatCard label="Total Cost Basis" value={fmt(totalInvested, market)}                 sub="acquisition cost" />
        <StatCard label="Unrealised Gain"  value={fmt(totalValue - totalInvested, market)}    sub="appreciation" />
        <StatCard label="Avg. Return"      value={<GainBadge pct={parseFloat(avgGain)} />}    sub="since acquisition" />
      </div>

      <div className="space-y-3">
        {data.map((a, i) => (
          <Card key={i} className="bg-white border-[#e5e5e5]">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#ffd879]/15 flex items-center justify-center shrink-0">
                    <Palette className="w-5 h-5 text-[#ffd879]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1a1a1a] text-sm">{a.name}</p>
                    <p className="text-xs text-[#4a4a4a] mt-0.5">{a.artist} · {a.category} · {a.purchase_year}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-right shrink-0">
                  <div>
                    <p className="text-xs text-[#4a4a4a]">Cost</p>
                    <p className="text-sm text-[#4a4a4a]">{fmt(a.purchase_price, market)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#4a4a4a]">Current Est.</p>
                    <p className="text-sm font-bold text-[#1a1a1a]">{fmt(a.current_value, market)}</p>
                    <GainBadge pct={a.gain_pct} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Badge className="bg-[#ffd879]/20 text-[#1a1a1a] border-0 text-xs">{a.type}</Badge>
                    {a.insured && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">Insured</Badge>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function IPTab({ data, market }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <Brain className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
        <p className="text-[#4a4a4a] text-sm">No intellectual property holdings found</p>
      </div>
    );
  }

  const totalValue    = data.reduce((s, d) => s + (d.current_value || 0), 0);
  const totalCost     = data.reduce((s, d) => s + (d.purchase_price || 0), 0);
  const totalRoyalty  = data.reduce((s, d) => s + (d.annual_royalty || 0), 0);
  const avgGain       = totalCost > 0 ? ((totalValue - totalCost) / totalCost * 100).toFixed(1) : '0.0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="IP Portfolio Value" value={fmt(totalValue, market)}   sub={`${data.length} assets`} />
        <StatCard label="Total Cost Basis"   value={fmt(totalCost, market)}    sub="acquisition cost" />
        <StatCard label="Annual Royalties"   value={fmt(totalRoyalty, market)} sub="recurring income" />
        <StatCard label="Overall Gain"       value={<GainBadge pct={parseFloat(avgGain)} />} sub="since acquisition" />
      </div>

      <div className="space-y-3">
        {data.map((d, i) => {
          const gainPct = d.gain_pct ?? (d.purchase_price ? ((d.current_value - d.purchase_price) / d.purchase_price * 100) : 0);
          return (
            <Card key={i} className="bg-white border-[#e5e5e5]">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#ffd879]/15 flex items-center justify-center shrink-0">
                      <Brain className="w-5 h-5 text-[#ffd879]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1a1a1a] text-sm">{d.name}</p>
                      <p className="text-xs text-[#4a4a4a] mt-0.5">
                        {d.type}{d.creator ? ` · ${d.creator}` : ''}
                        {d.royalty_rate ? ` · ${d.royalty_rate}% royalty` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-6 text-right shrink-0">
                    <div className="w-24">
                      <p className="text-xs text-[#4a4a4a] mb-1">Cost</p>
                      <p className="text-sm font-medium text-[#1a1a1a]">{fmt(d.purchase_price, market)}</p>
                    </div>
                    <div className="w-28">
                      <p className="text-xs text-[#4a4a4a] mb-1">Annual Royalty</p>
                      <p className="text-sm font-medium text-emerald-600">{fmt(d.annual_royalty, market)}</p>
                    </div>
                    <div className="w-28">
                      <p className="text-xs text-[#4a4a4a] mb-1">Current Est.</p>
                      <p className="text-sm font-bold text-[#1a1a1a]">{fmt(d.current_value, market)}</p>
                      <GainBadge pct={gainPct} />
                    </div>
                    <div className="w-24">
                      <p className="text-xs text-[#4a4a4a] mb-1">Category</p>
                      <Badge className="bg-[#ffd879]/20 text-[#1a1a1a] border-0 text-xs">{d.type}</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function OthersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const market = user?.market || 'IN';

  const activeTab = searchParams.get('tab') || 'overview';
  const handleTabChange = (tab) => setSearchParams({ tab });

  const [loading, setLoading] = useState(true);
  const [realEstate, setRealEstate] = useState([]);
  const [gold, setGold]             = useState([]);
  const [art, setArt]               = useState([]);
  const [ip, setIp]                 = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      setLoading(true);
      try {
        const [reRes, goldRes, artRes, ipRes] = await Promise.all([
          axios.get(`${API}/real-estate/portfolio`).catch(() => ({ data: null })),
          axios.get(`${API}/other-assets/gold`).catch(() => ({ data: [] })),
          axios.get(`${API}/other-assets/art`).catch(() => ({ data: [] })),
          axios.get(`${API}/other-assets/ip`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setRealEstate(reRes.data?.properties || []);
        setGold(goldRes.data || []);
        setArt(artRes.data || []);
        setIp(ipRes.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, []);

  const reValue   = realEstate.reduce((s, p) => s + (p.current_value || 0), 0);
  const goldValue = gold.reduce((s, g) => s + (g.units * g.current_price), 0);
  const artValue  = art.reduce((s, a) => s + (a.current_value || 0), 0);
  const ipValue   = ip.reduce((s, d) => s + (d.current_value || 0), 0);
  const totalAUM  = reValue + goldValue + artValue + ipValue;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <RefreshCw className="w-8 h-8 text-[#ffd87a] animate-spin" />
      </div>
    );
  }

  return (
    <div style={{ padding: '40px 46px 72px', background: '#f8f6f1', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, marginBottom: 26 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 46, height: 46, borderRadius: 13, background: '#d9e2c3', color: '#1a1a1a', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Coins size={20} />
          </div>
          <div>
            <div style={{ textTransform: 'uppercase', fontSize: 10, fontWeight: 600, letterSpacing: '0.13em', color: '#a8a8a8', marginBottom: 4 }}>
              Real Assets Portfolio · Total AUM {fmt(totalAUM, market)}
            </div>
            <h1 style={{ fontFamily: "'PerfectNineties', Georgia, serif", fontWeight: 500, fontSize: 34, letterSpacing: '-0.025em', lineHeight: 1.04, color: '#2a2a2a', margin: 0 }}>
              Others
            </h1>
          </div>
        </div>
        <button
          onClick={() => navigate('/import')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 18px', borderRadius: 99, background: '#ffd87a', color: '#000', fontWeight: 600, fontSize: 13.5, border: 'none', cursor: 'pointer' }}
        >
          <Plus size={16} /> Add Holdings
        </button>
      </div>

      {/* Cash & Alternatives moved to dedicated page */}
      <Link
        to="/cash-alternatives"
        className="flex items-center justify-between p-3 mb-6 rounded-lg border border-[#ffd87a]/40 bg-[#ffd87a]/5 hover:bg-[#ffd87a]/10 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-[#ffd87a]" />
          <span className="text-sm font-medium text-[#1a1a1a]">Looking for Cash & Alternatives?</span>
          <span className="text-sm text-[#4a4a4a]">They have their own dedicated dashboard now.</span>
        </div>
        <div className="flex items-center gap-1 text-sm font-medium text-[#1a1a1a]">
          View Cash & Alternatives <ChevronRight className="w-4 h-4" />
        </div>
      </Link>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="bg-[#f5f5f5] border border-[#e5e5e5] mb-6 h-auto gap-1 p-1 w-fit flex-wrap">
          <TabsTrigger value="overview" className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="real_estate" className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <Home className="w-3.5 h-3.5" /> Real Estate
          </TabsTrigger>
          <TabsTrigger value="gold" className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <Coins className="w-3.5 h-3.5" /> Gold & Commodities
          </TabsTrigger>
          <TabsTrigger value="art" className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <Palette className="w-3.5 h-3.5" /> Art & Collectibles
          </TabsTrigger>
          <TabsTrigger value="ip" className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <Brain className="w-3.5 h-3.5" /> Intellectual Property
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {(() => {
            const segments = [
              { label: 'Real Estate',        value: reValue },
              { label: 'Gold & Silver',      value: goldValue },
              { label: 'Art & Collectibles', value: artValue },
              { label: 'Intellectual Prop.', value: ipValue },
            ].filter(d => d.value > 0).map((d, i) => ({ ...d, color: CHART_PALETTE[i % CHART_PALETTE.length] }));
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Stat band */}
                <div style={{ display: 'flex', flexWrap: 'wrap', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  {[
                    { k: 'Total Value', v: fmt(totalAUM, market), delta: 'across all real assets' },
                    { k: 'Real Estate', v: fmt(reValue, market), delta: `${realEstate.length} properties` },
                    { k: 'Gold & Silver', v: fmt(goldValue, market), delta: `${gold.length} holdings` },
                    { k: 'Art & IP', v: fmt(artValue + ipValue, market), delta: `${art.length + ip.length} assets` },
                  ].map((cell, i) => (
                    <div key={i} style={{ flex: 1, minWidth: 130, padding: '14px 16px', borderLeft: i > 0 ? '1px solid #e5e5e5' : 'none', textAlign: 'center' }}>
                      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, color: '#a8a8a8' }}>{cell.k}</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', margin: '5px 0 4px', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{cell.v}</div>
                      <div style={{ fontSize: 10.5, color: '#888' }}>{cell.delta}</div>
                    </div>
                  ))}
                </div>

                {/* Charts */}
                {totalAUM > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, alignItems: 'stretch' }}>
                    <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a8a8a8', marginBottom: 14 }}>Allocation by Asset Type</div>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={segments.map(d => ({ name: d.label.replace('Intellectual Prop.','IP').replace('Art & Collectibles','Art'), value: d.value, color: d.color }))} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                          <XAxis dataKey="name" tick={{ fontSize: 10.5, fill: '#888' }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 9, fill: '#aaa' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1e7 ? `${(v/1e7).toFixed(0)}Cr` : v >= 1e5 ? `${(v/1e5).toFixed(0)}L` : v} />
                          <Tooltip formatter={(v) => [fmt(v, market), 'Value']} contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e5e5' }} />
                          <Bar dataKey="value" radius={[5,5,0,0]}>
                            {segments.map((d, i) => <Cell key={i} fill={d.color} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a8a8a8', marginBottom: 12 }}>Portfolio Split</div>
                      <div style={{ display: 'flex', alignItems: 'stretch', gap: 16, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          <PieChart width={150} height={200}>
                            <Pie data={segments.map(d => ({ name: d.label, value: d.value }))} cx={75} cy={100} innerRadius={52} outerRadius={72} dataKey="value" paddingAngle={3}>
                              {segments.map((d, i) => <Cell key={i} fill={d.color} />)}
                            </Pie>
                          </PieChart>
                        </div>
                        <ul style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0 }}>
                          {segments.map((d, i) => {
                            const pct = totalAUM > 0 ? d.value / totalAUM * 100 : 0;
                            return (
                              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 0', borderBottom: i < segments.length - 1 ? '1px solid #f0f0f0' : 'none', fontSize: 10.5 }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                                <span style={{ flex: 1, color: '#555' }}>{d.label}</span>
                                <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{pct < 0.1 ? '<0.1' : pct.toFixed(1)}%</span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick nav */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                  {[
                    { tab: 'real_estate', icon: Home,    label: 'Real Estate',        value: fmt(reValue, market),    sub: `${realEstate.length} properties` },
                    { tab: 'gold',        icon: Coins,   label: 'Gold & Commodities', value: fmt(goldValue, market),  sub: `${gold.length} holdings` },
                    { tab: 'art',         icon: Palette, label: 'Art & Collectibles', value: fmt(artValue, market),   sub: `${art.length} pieces` },
                    { tab: 'ip',          icon: Brain,   label: 'Intellectual Prop.',  value: fmt(ipValue, market),    sub: `${ip.length} assets` },
                  ].map(({ tab, icon: Icon, label, value, sub }) => (
                    <button key={tab} onClick={() => handleTabChange(tab)} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px',
                      background: '#fff', border: '1px solid #e5e5e5', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                      transition: 'border-color 0.2s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffd879'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e5e5'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(255,216,121,0.15)', display: 'grid', placeItems: 'center' }}>
                          <Icon size={14} style={{ color: '#c9a000' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>{label}</div>
                          <div style={{ fontSize: 10, color: '#888', marginTop: 1 }}>{sub}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a' }}>{value}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="real_estate">
          <RealEstateTab data={realEstate} market={market} />
        </TabsContent>
        <TabsContent value="gold">
          <GoldTab data={gold} market={market} />
        </TabsContent>
        <TabsContent value="art">
          <ArtTab data={art} market={market} />
        </TabsContent>
        <TabsContent value="ip">
          <IPTab data={ip} market={market} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
